import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import { WrapStoryboardsChange } from "@/store/zustand/changeLedgerTypes";
import {
  applyStoryboardDeltas,
  useIssuesStore,
} from "@/store/zustand/issues-store";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { toast } from "sonner";
import { updateAllStoreLookups } from "./navTreeLookupUtils";
import { NavTreeNode } from "@/utils/types";
import { TempoElement } from "@/utils/tempoElement";
import { updateCodebaseId } from "./utils";

const updateCodebaseIdsWithMap = (
  change: WrapStoryboardsChange,
  newSbId: string,
) => {
  const codebaseIdHashingKey = change.changeFields.codebaseIdHashingKey;
  if (!codebaseIdHashingKey) {
    return;
  }

  if (!change.changeFields.navTreeForInstantUpdate) {
    return;
  }

  let newHtml = change.changeFields.htmlForInstantUpdate;

  const updateCodebaseIds = (navTree: NavTreeNode) => {
    if (!navTree.tempoElement) {
      return;
    }

    const prevCodebaseId = navTree.tempoElement.codebaseId;
    let newCodebaseId;
    if (prevCodebaseId) {
      newCodebaseId = updateCodebaseId(prevCodebaseId, codebaseIdHashingKey);
      navTree.tempoElement = new TempoElement(
        newCodebaseId,
        newSbId,
        navTree.tempoElement.uniquePath,
      );
      newHtml = newHtml?.replace(
        new RegExp(prevCodebaseId, "g"),
        newCodebaseId,
      );
    }

    navTree.children?.forEach((child) => {
      updateCodebaseIds(child);
    });
  };

  updateCodebaseIds(change.changeFields.navTreeForInstantUpdate);

  change.changeFields.htmlForInstantUpdate = newHtml;
};

export const applyWrapStoryboardsUpdate = async (
  change: WrapStoryboardsChange,
) => {
  const canvasId = change.changeFields.canvasId;
  const subCanvas = useSupabaseStore.getState().subCanvases[canvasId];
  const mainCanvas = useIssuesStore.getState().currentCanvas[canvasId];

  if (!subCanvas && !mainCanvas) {
    toast.error("Canvas not found");
    return;
  }

  const supabaseClient = useSupabaseStore.getState().supabaseClient;
  if (!supabaseClient) {
    return;
  }

  const last_updated = new Date();

  const updateDict: Record<string, unknown> = {
    last_updated: last_updated.toISOString(),
  };

  if (change.changeFields?.htmlForInstantUpdate) {
    const sbId = change.changeFields.deltas[0].storyboardId;

    updateCodebaseIdsWithMap(change, sbId);

    useIframePoolStore
      .getState()
      .setHtmlForStoryboard(sbId, change.changeFields.htmlForInstantUpdate);

    // If used default, don't update the nav tree to show a loading state
    if (change.changeFields.navTreeForInstantUpdate) {
      updateAllStoreLookups(
        sbId,
        { navTree: change.changeFields.navTreeForInstantUpdate },
        true,
      );
    }
  }

  // This is a sub-canvas
  if (subCanvas) {
    const newItem = {
      ...subCanvas,
      ...updateDict,
    };

    const { newStoryboards, undoDeltas } = applyStoryboardDeltas(
      change.changeFields.deltas,
      newItem.storyboards,
    );
    newItem.storyboards = newStoryboards;
    change.instantUpdateData = {
      ...(change.instantUpdateData || {}),
      undoDeltas,
    };

    useSupabaseStore
      .getState()
      .onDbSnapshot("subCanvases", { newItems: [newItem] });

    await supabaseClient
      .from("sub_canvases")
      .update(updateDict)
      .eq("id", subCanvas.id);

    return;
  }

  await useIssuesStore
    .getState()
    .updateCanvasStoryboardsInIssuesStore(
      mainCanvas.id,
      change.changeFields.deltas,
      updateDict,
      change,
    );
};

export const undoWrapStoryboardsUpdate = async (
  change: WrapStoryboardsChange,
) => {
  const undoDeltas = change.instantUpdateData?.undoDeltas;
  if (!undoDeltas) {
    return;
  }

  const canvasId = change.changeFields.canvasId;
  const subCanvas = useSupabaseStore.getState().subCanvases[canvasId];
  const mainCanvas = useIssuesStore.getState().currentCanvas[canvasId];

  if (!subCanvas && !mainCanvas) {
    toast.error("Canvas not found");
    return;
  }

  const supabaseClient = useSupabaseStore.getState().supabaseClient;
  if (!supabaseClient) {
    return;
  }

  const last_updated = new Date();

  const updateDict: Record<string, unknown> = {
    last_updated: last_updated.toISOString(),
  };

  // This is a sub-canvas
  if (subCanvas) {
    const newItem = {
      ...subCanvas,
      ...updateDict,
    };

    const { newStoryboards } = applyStoryboardDeltas(
      undoDeltas,
      newItem.storyboards,
    );
    newItem.storyboards = newStoryboards;

    useSupabaseStore
      .getState()
      .onDbSnapshot("subCanvases", { newItems: [newItem] });

    await supabaseClient
      .from("sub_canvases")
      .update(updateDict)
      .eq("id", subCanvas.id);

    return;
  }

  await useIssuesStore
    .getState()
    .updateCanvasStoryboardsInIssuesStore(
      mainCanvas.id,
      undoDeltas,
      updateDict,
      undefined,
    );
};
